import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { alpha, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Divider from '@mui/material/Divider';
import Grow from '@mui/material/Grow';
import IconButton from '@mui/material/IconButton';
import InputBase from '@mui/material/InputBase';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListSubheader from '@mui/material/ListSubheader';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Typography from '@mui/material/Typography';
import SearchIcon from '@mui/icons-material/SearchRounded';
import CloseIcon from '@mui/icons-material/CloseRounded';
import NorthWestIcon from '@mui/icons-material/NorthWestRounded';
import ArrowForwardIcon from '@mui/icons-material/ArrowForwardRounded';
import { useTranslation } from 'react-i18next';

function Highlight({ text, q }) {
    if (text == null) return null;
    const value = String(text);
    if (!q) return value;
    const i = value.toLowerCase().indexOf(String(q).toLowerCase());
    if (i < 0) return value;
    return (
        <>
            {value.slice(0, i)}
            <Box component="mark" sx={{ bgcolor: 'transparent', color: 'secondary.main', fontWeight: 700, p: 0 }}>
                {value.slice(i, i + q.length)}
            </Box>
            {value.slice(i + q.length)}
        </>
    );
}

function flatten(data) {
    const rows = [];
    (data?.suggestions || []).forEach((s, i) => rows.push({ type: 'suggest', key: 's' + i, item: s }));
    (data?.groups || []).forEach((g) => {
        g.items.forEach((item, i) => rows.push({ type: 'record', key: g.token + i, item, group: g }));
    });
    if ((data?.total || 0) > 0) rows.push({ type: 'all', key: 'all' });
    return rows;
}

export default function GlobalSearch() {
    const { t } = useTranslation();
    const theme = useTheme();
    const navigate = useNavigate();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const anchorRef = useRef(null);
    const inputRef = useRef(null);
    const paperRef = useRef(null);
    const abortRef = useRef(null);
    const [query, setQuery] = useState('');
    const [focused, setFocused] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    const [active, setActive] = useState(0);

    const rows = useMemo(() => flatten(data), [data]);
    const open = focused && (query.trim() !== '' || (data != null && (data.suggestions?.length > 0 || data.groups?.length > 0)));
    const parsedQ = data?.parsed?.q || '';

    const run = useCallback(async (q) => {
        if (abortRef.current) abortRef.current.abort();
        const trimmed = (q || '').trim();
        if (trimmed === '') {
            setData(null);
            setLoading(false);
            return;
        }
        const controller = new AbortController();
        abortRef.current = controller;
        setLoading(true);
        try {
            const res = await axios.get('/api/globalsearch', {
                params: { q: trimmed, limit: 5 },
                signal: controller.signal
            });
            setData(res.data);
            setActive(0);
        } catch (err) {
            if (!axios.isCancel(err) && err.code !== 'ERR_CANCELED') setData(null);
        } finally {
            if (abortRef.current === controller) setLoading(false);
        }
    }, []);

    useEffect(() => {
        const handle = setTimeout(() => run(query), 220);
        return () => clearTimeout(handle);
    }, [query, run]);

    useEffect(() => {
        const onKey = (e) => {
            const mod = e.metaKey || e.ctrlKey;
            if (mod && e.key.toLowerCase() === 'k') {
                e.preventDefault();
                setMobileOpen(true);
                setFocused(true);
                inputRef.current?.focus();
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    const close = () => {
        setFocused(false);
        setMobileOpen(false);
    };

    const goRecord = (item) => {
        if (item?.href) {
            close();
            setQuery('');
            navigate(item.href);
        }
    };

    const goAll = () => {
        close();
        navigate('/search?q=' + encodeURIComponent(query.trim()));
    };

    const applySuggest = (item) => {
        setQuery(item.insert || '');
        setFocused(true);
        inputRef.current?.focus();
    };

    const activate = (row) => {
        if (row == null) return;
        if (row.type === 'suggest') applySuggest(row.item);
        else if (row.type === 'record') goRecord(row.item);
        else goAll();
    };

    const onKeyDown = (e) => {
        if (e.key === 'Escape') {
            close();
            inputRef.current?.blur();
            return;
        }
        if (e.key === 'Enter') {
            e.preventDefault();
            if (rows[active]) activate(rows[active]);
            else if (query.trim() !== '') goAll();
            return;
        }
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActive(i => Math.min(i + 1, Math.max(rows.length - 1, 0)));
        }
        if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActive(i => Math.max(i - 1, 0));
        }
    };

    const input = (
        <Box
            ref={anchorRef}
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1.5,
                height: 38,
                borderRadius: 3,
                bgcolor: alpha('#fff', 0.12),
                color: 'inherit',
                width: isMobile ? '100%' : '100%',
                maxWidth: isMobile ? 'none' : 560,
                border: '1px solid',
                borderColor: focused ? alpha('#fff', 0.45) : alpha('#fff', 0.18),
                transition: 'border-color 120ms ease, background-color 120ms ease',
                '&:hover': { borderColor: alpha('#fff', 0.4) }
            }}
        >
            <SearchIcon sx={{ fontSize: 20, opacity: 0.85, flexShrink: 0 }} />
            <InputBase
                inputRef={inputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onFocus={() => setFocused(true)}
                onKeyDown={onKeyDown}
                placeholder={t('searchPlaceholder')}
                fullWidth
                sx={{
                    color: 'inherit',
                    fontSize: '0.85rem',
                    '& input::placeholder': { color: 'inherit', opacity: 0.65 }
                }}
            />
            {!isMobile && query === '' && (
                <Chip
                    size="small"
                    label={navigator.platform?.includes('Mac') ? '⌘K' : 'Ctrl K'}
                    sx={{
                        height: 20,
                        fontSize: '0.65rem',
                        bgcolor: alpha('#fff', 0.12),
                        color: 'inherit',
                        '& .MuiChip-label': { px: 0.75 }
                    }}
                />
            )}
            {query !== '' && (
                <IconButton size="small" onClick={() => { setQuery(''); setData(null); inputRef.current?.focus(); }} sx={{ color: 'inherit', p: 0.25 }}>
                    <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
            )}
        </Box>
    );

    const menu = (
        <Popper
            open={open}
            anchorEl={anchorRef.current}
            placement="bottom-start"
            style={{ zIndex: theme.zIndex.modal, width: anchorRef.current?.offsetWidth || 480 }}
            transition
        >
            {({ TransitionProps }) => (
                <Grow {...TransitionProps} style={{ transformOrigin: 'top center' }}>
                    <Paper
                        ref={paperRef}
                        elevation={12}
                        sx={{
                            mt: 1,
                            overflow: 'hidden',
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: 'divider',
                            maxHeight: 480
                        }}
                    >
                        <Box sx={{ position: 'relative', overflow: 'auto', maxHeight: 480 }}>
                            {loading && (
                                <CircularProgress size={14} sx={{ position: 'absolute', top: 10, right: 10 }} />
                            )}
                            {data?.mode === 'entity' && data.suggestions?.length === 0 && (
                                <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1.5 }}>
                                    {t('searchHintEntities')}
                                </Typography>
                            )}
                            {data?.mode === 'field' && data.suggestions?.length === 0 && (
                                <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1.5 }}>
                                    {t('searchHintFields')}
                                </Typography>
                            )}
                            {data?.mode === 'search' && rows.length === 0 && !loading && query.trim().length >= 2 && (
                                <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1.5 }}>
                                    {t('searchNoResults')}
                                </Typography>
                            )}
                            <List disablePadding>
                                {(data?.suggestions || []).map((s, i) => {
                                    const idx = i;
                                    return (
                                        <ListItemButton
                                            key={'s' + i}
                                            selected={active === idx}
                                            onMouseDown={(e) => e.preventDefault()}
                                            onMouseEnter={() => setActive(idx)}
                                            onClick={() => applySuggest(s)}
                                            sx={{ py: 0.75, gap: 1.25 }}
                                        >
                                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: s.color || 'primary.main', flexShrink: 0 }} />
                                            <Typography variant="body2" sx={{ flex: 1, fontWeight: 600 }}>
                                                {s.kind === 'field' ? s.label : s.label}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">@{s.token}</Typography>
                                            <NorthWestIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                                        </ListItemButton>
                                    );
                                })}
                                {(data?.groups || []).map((g) => {
                                    const start = rows.findIndex(r => r.type === 'record' && r.group?.token === g.token);
                                    return (
                                        <Box key={g.token}>
                                            <ListSubheader
                                                disableSticky
                                                sx={{
                                                    lineHeight: '32px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                    bgcolor: alpha(g.color || theme.palette.primary.main, 0.08),
                                                    color: 'text.primary',
                                                    fontWeight: 700,
                                                    fontSize: '0.72rem',
                                                    letterSpacing: 0.4,
                                                    textTransform: 'uppercase'
                                                }}
                                            >
                                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: g.color || 'primary.main' }} />
                                                {g.label}
                                                <Chip size="small" label={g.hasMore ? g.items.length + '+' : g.items.length} sx={{ height: 18, ml: 'auto', fontSize: '0.65rem' }} />
                                            </ListSubheader>
                                            {g.items.map((item, i) => {
                                                const idx = start + i;
                                                return (
                                                    <ListItemButton
                                                        key={item.id}
                                                        selected={active === idx}
                                                        onMouseDown={(e) => e.preventDefault()}
                                                        onMouseEnter={() => setActive(idx)}
                                                        onClick={() => goRecord(item)}
                                                        sx={{ py: 0.9, pl: 2.5 }}
                                                    >
                                                        <Box sx={{ minWidth: 0, flex: 1 }}>
                                                            <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                                                                <Highlight text={item.label} q={parsedQ} />
                                                            </Typography>
                                                            {(item.sublabel || item.matchedField) && (
                                                                <Typography variant="caption" color="text.secondary" noWrap>
                                                                    {item.sublabel ? <Highlight text={item.sublabel} q={parsedQ} /> : null}
                                                                    {item.sublabel && item.matchedField ? ' · ' : ''}
                                                                    {item.matchedField ? t('searchMatched') + ' ' + item.matchedField : ''}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    </ListItemButton>
                                                );
                                            })}
                                        </Box>
                                    );
                                })}
                            </List>
                            {(data?.total || 0) > 0 && (
                                <>
                                    <Divider />
                                    <ListItemButton
                                        selected={rows[active]?.type === 'all'}
                                        onMouseDown={(e) => e.preventDefault()}
                                        onMouseEnter={() => setActive(rows.findIndex(r => r.type === 'all'))}
                                        onClick={goAll}
                                        sx={{ py: 1.1, justifyContent: 'space-between' }}
                                    >
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                                            {data.groups?.some(g => g.hasMore) ? t('seeAll') + ' — ' + t('searchResults') : t('searchSeeAll', { count: data.total })}
                                        </Typography>
                                        <ArrowForwardIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                                    </ListItemButton>
                                </>
                            )}
                        </Box>
                    </Paper>
                </Grow>
            )}
        </Popper>
    );

    if (isMobile && !mobileOpen) {
        return (
            <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-end' }}>
                <IconButton color="inherit" onClick={() => { setMobileOpen(true); setTimeout(() => inputRef.current?.focus(), 0); }}>
                    <SearchIcon />
                </IconButton>
            </Box>
        );
    }

    return (
        <ClickAwayListener onClickAway={(e) => { if (paperRef.current?.contains(e.target)) return; close(); }}>
            <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center', px: { xs: 1, md: 2 }, minWidth: 0 }}>
                {input}
                {menu}
            </Box>
        </ClickAwayListener>
    );
}
